import styled, {css} from 'styled-components';
import {getColorFromTheme} from '../../../../../../ui/ui-utils';
import {
  MemberCard,
  MemberSearchBar,
  MemberSearchIcon,
} from '../admin-member/member-redesign.styles';

const buttonReset = css`
  border: 0;
  font-family: Graphik, sans-serif;
  cursor: pointer;
`;

export const DetailPage = styled.section`
  width: 100%;
  max-width: 1120px;
  min-width: 0;
  margin: 0 auto;
  padding-bottom: 48px;
`;

export const Breadcrumbs = styled.nav`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
  color: ${getColorFromTheme('textBody')};
  font: 400 14px/22px Graphik, sans-serif;

  button {
    ${buttonReset};
    padding: 0;
    background: transparent;
    color: ${getColorFromTheme('purple300')};
  }

  span:last-child {
    overflow: hidden;
    color: ${getColorFromTheme('blackTwo')};
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const SummaryCard = styled.section`
  padding: 24px;
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 10px;
  background: ${getColorFromTheme('white')};
`;

export const SummaryHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;

  @media (max-width: 620px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

export const SummaryHeading = styled.div`
  min-width: 0;
`;

export const TitleLine = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

export const GroupTitle = styled.h1`
  overflow: hidden;
  margin: 0;
  color: ${getColorFromTheme('blackTwo')};
  font: 600 24px/32px Graphik, sans-serif;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const TypePill = styled.span`
  padding: 6px 10px;
  border-radius: 7px;
  background: ${getColorFromTheme('backgroundDark')};
  color: ${getColorFromTheme('textBody')};
  font: 500 12px/18px Graphik, sans-serif;
`;

export const ReadOnlyPill = styled(TypePill)`
  display: inline-flex;
  align-items: center;
  gap: 5px;

  svg {
    width: 12px;
    height: 12px;
  }
`;

export const MemberCount = styled.p`
  margin: 8px 0 0;
  color: ${getColorFromTheme('textBody')};
  font: 400 14px/22px Graphik, sans-serif;
`;

export const EditButton = styled.button`
  ${buttonReset};
  min-height: 42px;
  padding: 10px 18px;
  border: 1px solid ${getColorFromTheme('borderDark')};
  border-radius: 8px;
  background: ${getColorFromTheme('white')};
  color: ${getColorFromTheme('blackTwo')};
  font: 500 14px/22px Graphik, sans-serif;

  &:hover {
    background: ${getColorFromTheme('backgroundLight')};
  }
`;

export const LeadSection = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  margin-top: 22px;
  padding-top: 20px;
  border-top: 1px solid ${getColorFromTheme('borderLight')};

  @media (max-width: 620px) {
    align-items: flex-start;
    flex-direction: column;
    gap: 10px;
  }
`;

export const LeadLabel = styled.span`
  flex: 0 0 auto;
  color: ${getColorFromTheme('textBody')};
  font: 400 14px/22px Graphik, sans-serif;
`;

export const LeadIdentity = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 10px;
`;

export const LeadCopy = styled.div`
  min-width: 0;
`;

export const LeadName = styled.strong`
  display: block;
  overflow: hidden;
  color: ${getColorFromTheme('blackTwo')};
  font: 600 14px/20px Graphik, sans-serif;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const LeadMeta = styled.span`
  display: block;
  color: ${getColorFromTheme('textBody')};
  font: 400 12px/18px Graphik, sans-serif;
`;

export const InlineMemberSearchBar = styled(MemberSearchBar)`
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 620px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

export const GroupMemberCard = styled(MemberCard)`
  margin-top: 20px;
`;

export const InlineMemberSearchField = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;

  ${MemberSearchIcon} {
    top: 14px;
    right: 16px;
  }
`;

export const SearchWrap = styled.div`
  position: relative;
  flex: 1;

  input {
    width: 100%;
    height: 44px;
    padding: 10px 44px 10px 16px;
    border: 1px solid ${getColorFromTheme('borderDark')};
    border-radius: 6px;
    outline: none;
    background: ${getColorFromTheme('white')};
    color: ${getColorFromTheme('blackTwo')};
    font: 400 16px/24px Graphik, sans-serif;

    &::placeholder {
      color: ${getColorFromTheme('textBody')};
    }

    &:focus {
      border-color: ${getColorFromTheme('purple300')};
      box-shadow: 0 0 0 3px rgba(88, 90, 223, 0.12);
    }
  }

  svg {
    position: absolute;
    top: 14px;
    right: 14px;
    width: 16px;
    height: 16px;
    pointer-events: none;
  }
`;

export const PrimaryButton = styled.button`
  ${buttonReset};
  flex: 0 0 auto;
  min-height: 44px;
  padding: 10px 18px;
  border-radius: 8px;
  background: ${getColorFromTheme('purple300')};
  color: ${getColorFromTheme('alwaysWhite')};
  font: 600 14px/22px Graphik, sans-serif;

  &:hover:not(:disabled) {
    background: ${getColorFromTheme('purple350')};
  }

  &:disabled {
    cursor: not-allowed;
    background: ${getColorFromTheme('backgroundDark')};
    color: ${getColorFromTheme('textMuted')};
  }
`;

export const EmptyState = styled.div`
  padding: 52px 24px;
  color: ${getColorFromTheme('textBody')};
  text-align: center;
  font: 400 14px/22px Graphik, sans-serif;
`;

export const ModalShell = styled.div`
  width: min(520px, 100%);
  max-height: min(680px, calc(100vh - 48px));
  overflow: hidden;
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 12px;
  background: ${getColorFromTheme('white')};
  box-shadow: 0 16px 48px rgba(30, 30, 47, 0.16);
`;

export const ModalBody = styled.div`
  padding: 16px 20px 0;
`;

export const CandidateViewport = styled.div`
  position: relative;
  min-height: 0;
  margin-top: 12px;
`;

export const CandidateList = styled.div`
  max-height: 330px;
  padding: 2px 0 12px;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-color: ${getColorFromTheme('borderDark')} transparent;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 6px;
    background: ${getColorFromTheme('borderDark')};
  }
`;

export const CandidateFade = styled.span<{$position: 'top' | 'bottom'}>`
  position: absolute;
  z-index: 2;
  ${({$position}) => ($position === 'top' ? 'top: 0;' : 'bottom: 0;')}
  right: 0;
  left: 0;
  height: 38px;
  background: ${({$position, theme}) =>
    $position === 'top'
      ? `linear-gradient(${theme.colors.white}, transparent)`
      : `linear-gradient(transparent, ${theme.colors.white})`};
  pointer-events: none;
`;

export const CandidateRow = styled.label<{$selected: boolean}>`
  display: grid;
  min-height: 58px;
  grid-template-columns: 20px 36px minmax(0, 1fr);
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  background: ${({$selected}) =>
    $selected
      ? getColorFromTheme('backgroundDark')
      : getColorFromTheme('white')};
  cursor: pointer;

  &:hover {
    background: ${getColorFromTheme('backgroundLight')};
  }
`;

export const CandidateCopy = styled.span`
  min-width: 0;
`;

export const CandidateNameLine = styled.span`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 8px;
`;

export const CandidateName = styled.strong`
  overflow: hidden;
  color: ${getColorFromTheme('blackTwo')};
  font: 500 14px/20px Graphik, sans-serif;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const CandidateMeta = styled.span`
  display: block;
  overflow: hidden;
  color: ${getColorFromTheme('textBody')};
  font: 400 12px/18px Graphik, sans-serif;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const SelectedCount = styled.span`
  margin-right: auto;
  color: ${getColorFromTheme('textBody')};
  font: 500 13px/20px Graphik, sans-serif;
`;

export const ErrorCopy = styled.p`
  margin: 10px 0 0;
  color: ${getColorFromTheme('red400')};
  font: 400 12px/18px Graphik, sans-serif;
`;

export const ConfirmationText = styled.p`
  margin: 0;
  color: ${getColorFromTheme('textBody')};
  line-height: 1.6;
`;
